import { useMsal } from '@azure/msal-react';
import { useSearch } from '@tanstack/react-router';
import { createFileRoute } from '@tanstack/react-router';
import { SystemIcon, Typography } from '@volvo/vcdk-react';
import { useTranslation } from 'react-i18next';

import Blurb from './_components/Blurb';
import CardSection from './_components/CardSection';
import Hero from './_components/Hero';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { loginRequest } from '@/config/MsalAuthConfig';
import { useCanonical } from '@/hooks/useCanonical';

export const Route = createFileRoute('/_public/login/')({
  component: LoginPage,
  errorComponent: ErrorBoundary,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      redirectTo: (search.redirectTo as string) || undefined,
    };
  },
});

function LoginPage() {
  const { redirectTo } = useSearch({
    from: '/_public/login/',
  });
  const { instance } = useMsal();
  const { t } = useTranslation();
  useCanonical('/connect/login');

  const handleLogin = async () => {
    try {
      instance.loginRedirect({
        ...loginRequest,
        ...(redirectTo ? { redirectStartPage: redirectTo } : {}),
      });
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  return (
    <>
      <Hero>
        <h1 className="mb-4 text-center font-serif text-xl font-extralight text-white md:text-3xl">
          {t(($) => $.pages.login.hero_title)}
        </h1>
        <p className="mb-8 max-w-[645px] text-center font-serif text-base font-extralight text-white">
          {t(($) => $.pages.login.hero_body)}
        </p>
        <button
          className="rounded-xlarge flex cursor-pointer flex-row items-center justify-center gap-2 border border-white px-6 py-3 text-balance text-white hover:bg-black"
          onClick={handleLogin}
        >
          {t(($) => $.pages.login.log_in)}
          <SystemIcon icon="arrow-right" invertTheme />
        </button>
      </Hero>
      <div className="border-l-neutral-light mx-auto grid w-full max-w-xl grid-cols-1 border-l px-8 py-12 md:grid-cols-12 md:py-13">
        <div className="md:col-span-7">
          <a href='https://volvopenta.com"' className="mb-8 flex gap-4 hover:underline">
            <SystemIcon icon="arrow-left" className="text-secondary" />
            <Typography variant="body" className="text-secondary">
              {t(($) => $.pages.login.blurb_back_button)}
            </Typography>
          </a>
          <h2 className="font-serif text-xl leading-normal font-extralight md:text-3xl">
            <b>{t(($) => $.pages.login.blurb_bolded_main_text)}</b> {t(($) => $.pages.login.blurb_regular_main_text)}
          </h2>
        </div>
        <div className="flex justify-end md:col-span-5">
          <Typography className="text-secondary">Volvo Penta Connect</Typography>
        </div>
      </div>
      <div className="mx-auto grid w-full max-w-xl grid-cols-1 md:grid-cols-3">
        <Blurb
          title={t(($) => $.pages.login.CTAs.service_title)}
          label={t(($) => $.pages.login.CTAs.service_label)}
          body={t(($) => $.pages.login.CTAs.service_body)}
        />
        <Blurb
          title={t(($) => $.pages.login.CTAs.manuals_title)}
          label={t(($) => $.pages.login.CTAs.manuals_label)}
          body={t(($) => $.pages.login.CTAs.manuals_body)}
        />
        <Blurb
          title={t(($) => $.pages.login.CTAs.efficiency_title)}
          label={t(($) => $.pages.login.CTAs.efficiency_label)}
          body={t(($) => $.pages.login.CTAs.efficiency_body)}
        />
      </div>
      <div className="border-l-neutral-light mx-auto w-full max-w-xl border-l py-12 md:py-13">
        <CardSection
          cards={[
            {
              title: t(($) => $.pages.login.cards.connect_title),
              label: t(($) => $.pages.login.cards.connect_label),
              body: t(($) => $.pages.login.cards.connect_body),
              media: {
                alt: 'Image of person using vp connect',
                src: 'https://www.volvopenta.com/-/media/volvopenta/industrial/vp-connect.jpg?mh=1055&mw=1860&v=gyBlPw&c=1&cw=1860&ch=1046&jq=80&hash=EC5815CF8597C0B2E9144021BDE0803A',
              },
            },
            {
              title: t(($) => $.pages.login.cards.uptime_title),
              label: t(($) => $.pages.login.cards.uptime_label),
              body: t(($) => $.pages.login.cards.uptime_body),
              media: {
                alt: 'Image of woman analyzing oil',
                src: 'https://www.volvopenta.com/-/media/volvopenta/service-and-support/oil-analysis/cover.jpg?mh=1805&mw=2880&v=yklePw&c=1&cw=2880&ch=1620&jq=80&hash=14C4AE922C57BDD275999853B3AFC728',
              },
            },
          ]}
        />
      </div>
    </>
  );
}
